import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import RefreshIcon from '@mui/icons-material/Refresh';

import {
  extractYouTubeId,
  resolveYouTubeVideo,
  searchYouTubeVideos,
  YouTubeServiceError,
  type YouTubeErrorCode,
  type YouTubeResolved,
  type YouTubeSearchResult,
} from '../services/youtubeService';

export interface VideoSelection {
  videoId: string;
  title: string;
  channelName: string;
  thumbnailUrl: string;
  /** Optional: surfaced when present so the caller can warn the teacher. */
  regionBlocked?: string[] | null;
}

interface VideoLibraryDialogProps {
  open: boolean;
  onClose: () => void;
  onSelectVideo: (selection: VideoSelection) => void;
  initialSearchTerm?: string;
  initialTab?: 'search' | 'url';
}

const ERROR_MESSAGES: Record<YouTubeErrorCode, string> = {
  NOT_FOUND: "We couldn't find that video. Check the URL.",
  PRIVATE: "That video is private and can't be embedded.",
  NOT_EMBEDDABLE: 'The uploader disabled embedding for this video.',
  UPSTREAM_ERROR: "YouTube didn't respond. Try again in a moment.",
  NOT_CONFIGURED: 'YouTube search is not configured on this server.',
  VALIDATION_ERROR: 'That URL is not a valid YouTube link.',
  UNAUTHORIZED: 'Please sign in again to search YouTube.',
  UNKNOWN: 'Something went wrong. Please try again.',
};

function errorMessage(err: unknown): string {
  if (err instanceof YouTubeServiceError) return ERROR_MESSAGES[err.code];
  if (err instanceof Error) return err.message;
  return ERROR_MESSAGES.UNKNOWN;
}

function toSelection(item: YouTubeSearchResult): VideoSelection {
  return {
    videoId: item.videoId,
    title: item.title,
    channelName: item.channelName,
    thumbnailUrl: item.thumbnailUrl ?? `https://i.ytimg.com/vi/${item.videoId}/mqdefault.jpg`,
  };
}

function resolvedToSelection(r: YouTubeResolved): VideoSelection {
  return {
    videoId: r.videoId,
    title: r.title,
    channelName: r.channelName,
    thumbnailUrl: r.thumbnailUrl ?? `https://i.ytimg.com/vi/${r.videoId}/mqdefault.jpg`,
    regionBlocked: r.regionBlocked,
  };
}

const VideoLibraryDialog: React.FC<VideoLibraryDialogProps> = ({
  open,
  onClose,
  onSelectVideo,
  initialSearchTerm = '',
  initialTab = 'search',
}) => {
  const [activeTab, setActiveTab] = React.useState<'search' | 'url'>(initialTab);
  const [searchTerm, setSearchTerm] = React.useState(initialSearchTerm);
  const [searchResults, setSearchResults] = React.useState<YouTubeSearchResult[]>([]);
  const [nextPageToken, setNextPageToken] = React.useState<string | null>(null);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [searchError, setSearchError] = React.useState<string | null>(null);
  const [selectingVideoId, setSelectingVideoId] = React.useState<string | null>(null);

  const [urlInput, setUrlInput] = React.useState('');
  const [urlLoading, setUrlLoading] = React.useState(false);
  const [urlError, setUrlError] = React.useState<string | null>(null);
  const lastInitialSearchTermRef = React.useRef<string | null>(null);
  const selectionInFlightRef = React.useRef(false);
  const selectionRequestIdRef = React.useRef(0);

  const handleSearch = React.useCallback(async (query: string, pageToken?: string) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    setSearchLoading(true);
    setSearchError(null);
    try {
      const page = await searchYouTubeVideos(trimmed, pageToken);
      setSearchResults((prev) => (pageToken ? [...prev, ...page.items] : page.items));
      setNextPageToken(page.nextPageToken);
    } catch (err) {
      setSearchError(errorMessage(err));
    } finally {
      setSearchLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!open) return;
    const nextTerm = initialSearchTerm.trim();
    const isNewInitial =
      lastInitialSearchTermRef.current === null || lastInitialSearchTermRef.current !== nextTerm;
    lastInitialSearchTermRef.current = nextTerm;
    setActiveTab(initialTab);
    setSearchError(null);
    setUrlError(null);
    if (!isNewInitial) return;
    setSearchTerm(nextTerm);
    setSearchResults([]);
    setNextPageToken(null);
    if (nextTerm) void handleSearch(nextTerm);
  }, [open, initialSearchTerm, initialTab, handleSearch]);

  const handleSelectResult = async (item: YouTubeSearchResult) => {
    if (selectionInFlightRef.current) return;
    selectionInFlightRef.current = true;
    const requestId = selectionRequestIdRef.current + 1;
    selectionRequestIdRef.current = requestId;
    setSelectingVideoId(item.videoId);
    // Defensively re-validate via /resolve before invoking onSelectVideo
    // — catches search rows whose uploader flipped embedding off after upload.
    setSearchError(null);
    try {
      const resolved = await resolveYouTubeVideo(item.videoId);
      if (selectionRequestIdRef.current !== requestId) return;
      onSelectVideo(resolvedToSelection(resolved));
      onClose();
    } catch (err) {
      if (selectionRequestIdRef.current !== requestId) return;
      // Fall back to the search-result snapshot on resolve transient failures
      // so the teacher isn't blocked by an upstream hiccup.
      if (err instanceof YouTubeServiceError && err.code === 'UPSTREAM_ERROR') {
        onSelectVideo(toSelection(item));
        onClose();
        return;
      }
      setSearchError(errorMessage(err));
    } finally {
      if (selectionRequestIdRef.current === requestId) {
        selectionInFlightRef.current = false;
        setSelectingVideoId(null);
      }
    }
  };

  const handleSubmitUrl = async () => {
    const id = extractYouTubeId(urlInput);
    if (!id) {
      setUrlError(ERROR_MESSAGES.VALIDATION_ERROR);
      return;
    }
    setUrlLoading(true);
    setUrlError(null);
    try {
      const resolved = await resolveYouTubeVideo(id);
      onSelectVideo(resolvedToSelection(resolved));
      onClose();
    } catch (err) {
      setUrlError(errorMessage(err));
    } finally {
      setUrlLoading(false);
    }
  };

  const handleClose = () => {
    setSearchError(null);
    setUrlError(null);
    setSearchLoading(false);
    setUrlLoading(false);
    selectionRequestIdRef.current += 1;
    selectionInFlightRef.current = false;
    setSelectingVideoId(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      sx={{ '& .MuiDialog-paper': { height: '80vh', maxHeight: '80vh' } }}
    >
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0 }}
      >
        <Typography variant="h6">Insert YouTube Video</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_e, value) => setActiveTab(value as 'search' | 'url')}
          sx={{ mb: 2 }}
        >
          <Tab value="search" label="Search YouTube" icon={<SearchIcon />} iconPosition="start" />
          <Tab value="url" label="From URL" icon={<LinkIcon />} iconPosition="start" />
        </Tabs>

        {activeTab === 'search' && (
          <>
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                void handleSearch(searchTerm);
              }}
              sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}
            >
              <TextField
                fullWidth
                variant="outlined"
                sx={{
                  flex: '1 1 320px',
                  '& .MuiOutlinedInput-input': { py: 1.25 },
                }}
                placeholder="Search YouTube videos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setSearchTerm('')} size="small">
                        <CloseIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={!searchTerm.trim() || searchLoading}
                startIcon={searchLoading ? <CircularProgress size={16} /> : <SearchIcon />}
              >
                {searchLoading ? 'Searching...' : 'Search'}
              </Button>
            </Box>

            {searchError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {searchError}
              </Alert>
            )}

            {searchResults.length > 0 && (
              <Box sx={{ height: 'calc(80vh - 240px)', overflowY: 'auto' }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                    gap: 2,
                  }}
                >
                  {searchResults.map((item) => {
                    const isSelecting = selectingVideoId === item.videoId;
                    const selectionDisabled = selectingVideoId !== null;

                    return (
                      <Card
                        key={item.videoId}
                        aria-disabled={selectionDisabled}
                        sx={{
                          cursor: selectionDisabled ? 'default' : 'pointer',
                          opacity: selectionDisabled && !isSelecting ? 0.55 : 1,
                          position: 'relative',
                          transition: 'transform 0.2s, box-shadow 0.2s, opacity 0.2s',
                          ...(selectionDisabled
                            ? {}
                            : { '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 } }),
                        }}
                        onClick={() => {
                          if (selectionDisabled) return;
                          void handleSelectResult(item);
                        }}
                      >
                        <CardMedia
                          component="img"
                          height="135"
                          image={
                            item.thumbnailUrl ??
                            `https://i.ytimg.com/vi/${item.videoId}/mqdefault.jpg`
                          }
                          alt={item.title}
                          sx={{ objectFit: 'cover' }}
                        />
                        {isSelecting && (
                          <Box
                            sx={{
                              alignItems: 'center',
                              bgcolor: 'rgba(255, 255, 255, 0.72)',
                              display: 'flex',
                              inset: 0,
                              justifyContent: 'center',
                              position: 'absolute',
                            }}
                          >
                            <CircularProgress size={28} />
                          </Box>
                        )}
                        <CardContent sx={{ p: 1.5 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, lineHeight: 1.25 }}
                            title={item.title}
                            // Two-line clamp so cards stay even
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {item.title}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{ display: 'block', mt: 0.5 }}
                          >
                            {item.channelName}
                          </Typography>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>

                {nextPageToken && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => void handleSearch(searchTerm, nextPageToken)}
                      disabled={searchLoading}
                      startIcon={searchLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                    >
                      {searchLoading ? 'Loading...' : 'Load More'}
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}

        {activeTab === 'url' && (
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmitUrl();
            }}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2, maxWidth: 640 }}
          >
            <Typography variant="body2" color="text.secondary">
              Paste a YouTube link.
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="https://www.youtube.com/watch?v=..."
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon />
                  </InputAdornment>
                ),
              }}
            />
            {urlError && <Alert severity="error">{urlError}</Alert>}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                type="submit"
                variant="contained"
                disabled={!urlInput.trim() || urlLoading}
                startIcon={urlLoading ? <CircularProgress size={16} /> : null}
              >
                {urlLoading ? 'Validating...' : 'Add video'}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default VideoLibraryDialog;
